import axios from 'axios';
import { toast } from 'sonner';
import { getAuthToken, clearAuthToken } from './tokenStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Small throttle so a burst of 402s (e.g. a page issuing 3 AI calls in parallel)
// only surfaces ONE toast instead of stacking three identical ones.
let _last402ToastAt = 0;
const CAP_TOAST_THROTTLE_MS = 4000;

// iter224 hotfix — Auto-retry idempotent requests on transient 5xx / network
// errors. During production pod-restart windows the origin returns Cloudflare
// 520 for 2-8s while uvicorn boots; a single silent retry with backoff means
// the user typically never sees the error. GET/HEAD/OPTIONS only so we NEVER
// double-submit a POST/PATCH/DELETE (side-effect risk).
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
const RETRY_METHODS = new Set(['get', 'head', 'options']);
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 400;  // 400ms → 1200ms (exp backoff base 3)

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and trial expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // iter224 hotfix — Auto-retry BEFORE any other error handling.
    // Runs at the TOP so a successful retry short-circuits the 4xx/toast
    // logic below. If retry exhausts, the original error falls through to
    // the existing TRIAL_EXPIRED / 402 / 401 branches unchanged.
    const config = error.config;
    if (config) {
      const method = (config.method || 'get').toLowerCase();
      const isNetworkError = !error.response;
      const status = error.response?.status;
      const isTransient = isNetworkError || (status && RETRYABLE_STATUSES.has(status));
      const isIdempotent = RETRY_METHODS.has(method);
      // Skip retries when: not a GET-family verb, not a transient error,
      // or we've already burned our retry budget.
      if (isIdempotent && isTransient) {
        config.__retryCount = (config.__retryCount || 0) + 1;
        if (config.__retryCount <= MAX_RETRIES) {
          // Exponential backoff: 400ms → 1200ms → (never hit — capped at 2)
          const delay = RETRY_BASE_MS * Math.pow(3, config.__retryCount - 1);
          await new Promise((r) => setTimeout(r, delay));
          try {
            return await api(config);  // Resolve with the retry's response
          } catch (retryErr) {
            error = retryErr;  // Fall through to existing handlers with the final failure
          }
        }
      }
    }

    // Trial expired — redirect to subscription page
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'TRIAL_EXPIRED'
    ) {
      if (
        !window.location.pathname.startsWith('/dashboard/subscription') &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/register')
      ) {
        window.location.href = '/dashboard/subscription';
      }
      return Promise.reject(error);
    }

    // iter77 — inline paywall CTA for AI credit cap.
    // Backend now returns 402 with detail.code === 'credit_cap_reached' and
    // detail.buy_credits_url when both monthly plan quota AND wallet are empty.
    // Show a sonner toast with an inline "Buy credits" action that deep-links
    // to the Subscription page. Throttled so parallel 402s don't stack.
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail || error.response?.data || {};
      const code = detail.code || error.response?.data?.code;
      const buyUrl = detail.buy_credits_url || '/dashboard/subscription';
      const now = Date.now();
      if (
        code === 'credit_cap_reached' &&
        now - _last402ToastAt > CAP_TOAST_THROTTLE_MS
      ) {
        _last402ToastAt = now;
        const message = detail.message
          || 'Monthly AI credit cap reached. Buy a credit pack to keep going — no plan change needed.';
        toast.error(message, {
          duration: 8000,
          action: {
            label: 'Buy credits',
            onClick: () => {
              if (!window.location.pathname.startsWith('/dashboard/subscription')) {
                window.location.href = buyUrl;
              }
            },
          },
        });
      }
    }

    if (error.response?.status === 401) {
      // Token is invalid or expired
      clearAuthToken();
      // Redirect to login if not already there
      if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export { api };
export default api;
